import React from 'react';
import { ThemeSelector } from './ThemeSelector';
import { Search, Map, Shield, BookOpen, Skull, Globe, LogOut, Edit3 } from 'lucide-react';

interface NavbarProps {
  isAdmin: boolean;
  onLogoutAdmin?: () => void;
  onOpenSearch: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  isAdmin,
  onLogoutAdmin,
  onOpenSearch,
}) => {
  const navigateTo = (e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
    e.preventDefault();
    window.history.pushState(null, '', url);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <header className="site-header sticky-top">
      <nav className="navbar navbar-expand-xl py-2">
        <div className="container-fluid px-lg-4">
          <a
            className="navbar-brand d-flex align-items-center gap-2 fw-bold text-decoration-none"
            href="/"
            onClick={(e) => navigateTo(e, '/')}
          >
            <img src="/img/favicons/favicon.png" alt="Logo" width="30" height="30" className="d-inline-block align-text-top" />
            <span className="brand-title">Echocondria</span>
          </a>

          <button
            className="navbar-toggler border-0"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarMainCollapse"
            aria-controls="navbarMainCollapse"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div className="collapse navbar-collapse" id="navbarMainCollapse">
            <ul className="navbar-nav me-auto mb-2 mb-xl-0 gap-xl-1">
              <li className="nav-item">
                <a className="nav-link" href="/" onClick={(e) => navigateTo(e, '/')}>
                  Home
                </a>
              </li>

              {/* Atlas Dropdown */}
              <li className="nav-item dropdown">
                <a className="nav-link dropdown-toggle d-flex align-items-center gap-1" href="/maps" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                  <Map size={16} /> Maps
                </a>
                <ul className="dropdown-menu">
                  <li><a className="dropdown-item" href="/maps" onClick={(e) => navigateTo(e, '/maps')}>All Maps</a></li>
                  <li><hr className="dropdown-divider" /></li>
                  <li><a className="dropdown-item" href="/maps/echocondria" onClick={(e) => navigateTo(e, '/maps/echocondria')}>Echocondria City</a></li>
                </ul>
              </li>

              {/* Character Rules Dropdown */}
              <li className="nav-item dropdown">
                <a className="nav-link dropdown-toggle d-flex align-items-center gap-1" href="/classes" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                  <Shield size={16} /> Rules & Options
                </a>
                <ul className="dropdown-menu">
                  <li><a className="dropdown-item" href="/classes" onClick={(e) => navigateTo(e, '/classes')}>Classes</a></li>
                  <li><a className="dropdown-item" href="/races" onClick={(e) => navigateTo(e, '/races')}>Races</a></li>
                  <li><a className="dropdown-item" href="/skills" onClick={(e) => navigateTo(e, '/skills')}>Skills</a></li>
                  <li><a className="dropdown-item" href="/abilities" onClick={(e) => navigateTo(e, '/abilities')}>Abilities</a></li>
                  <li><a className="dropdown-item" href="/backgrounds" onClick={(e) => navigateTo(e, '/backgrounds')}>Backgrounds</a></li>
                </ul>
              </li>

              {/* World & Locations */}
              <li className="nav-item dropdown">
                <a className="nav-link dropdown-toggle d-flex align-items-center gap-1" href="/cities" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                  <Globe size={16} /> World & Lore
                </a>
                <ul className="dropdown-menu">
                  <li><a className="dropdown-item" href="/cities" onClick={(e) => navigateTo(e, '/cities')}>Cities</a></li>
                  <li><a className="dropdown-item" href="/city-venues" onClick={(e) => navigateTo(e, '/city-venues')}>City Venues</a></li>
                  <li><a className="dropdown-item" href="/city-districts" onClick={(e) => navigateTo(e, '/city-districts')}>City Districts</a></li>
                  <li><a className="dropdown-item" href="/organizations" onClick={(e) => navigateTo(e, '/organizations')}>Organizations</a></li>
                  <li><a className="dropdown-item" href="/haunts" onClick={(e) => navigateTo(e, '/haunts')}>Haunts</a></li>
                </ul>
              </li>

              {/* Bestiary & Combat */}
              <li className="nav-item dropdown">
                <a className="nav-link dropdown-toggle d-flex align-items-center gap-1" href="/enemies" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                  <Skull size={16} /> Bestiary
                </a>
                <ul className="dropdown-menu">
                  <li><a className="dropdown-item" href="/enemies" onClick={(e) => navigateTo(e, '/enemies')}>Enemies</a></li>
                  <li><a className="dropdown-item" href="/animals" onClick={(e) => navigateTo(e, '/animals')}>Animals</a></li>
                  <li><a className="dropdown-item" href="/conditions" onClick={(e) => navigateTo(e, '/conditions')}>Conditions</a></li>
                </ul>
              </li>

              {/* Compendium */}
              <li className="nav-item dropdown">
                <a className="nav-link dropdown-toggle d-flex align-items-center gap-1" href="/items" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                  <BookOpen size={16} /> Compendium
                </a>
                <ul className="dropdown-menu">
                  <li><a className="dropdown-item" href="/items" onClick={(e) => navigateTo(e, '/items')}>Items & Gear</a></li>
                  <li><a className="dropdown-item" href="/quests" onClick={(e) => navigateTo(e, '/quests')}>Quests</a></li>
                  <li><a className="dropdown-item" href="/npcs" onClick={(e) => navigateTo(e, '/npcs')}>NPCs</a></li>
                  <li><a className="dropdown-item" href="/lexicon" onClick={(e) => navigateTo(e, '/lexicon')}>Lexicon</a></li>
                  <li><a className="dropdown-item" href="/tags" onClick={(e) => navigateTo(e, '/tags')}>Tags</a></li>
                </ul>
              </li>
            </ul>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              {/* Quick Search Button */}
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
                onClick={onOpenSearch}
                title="Search database (Ctrl+K)"
              >
                <Search size={14} />
                <span className="d-none d-md-inline">Search</span>
                <kbd className="badge bg-secondary-subtle small px-1 py-0 rounded">Ctrl+K</kbd>
              </button>

              {/* Theme Selector */}
              <ThemeSelector />

              {/* Admin Active Indicator & Exit Button (Hidden unless admin mode is active) */}
              {isAdmin && (
                <button
                  type="button"
                  className="btn btn-warning btn-sm d-flex align-items-center gap-1 shadow-sm"
                  onClick={onLogoutAdmin}
                  title="Exit Admin Mode (Lock Session)"
                >
                  <Edit3 size={14} />
                  <span className="small fw-semibold d-none d-sm-inline">Admin Active</span>
                  <LogOut size={13} className="ms-1 opacity-75" />
                </button>
              )}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};
